import React, { useCallback, useMemo } from "react";
import { View, StyleSheet, LayoutAnimation } from "react-native";
import { Baby } from "lucide-react-native";
import { CounterInput } from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { BasicInfoQuestionLayout, YesNoOptions } from "../components";
import { useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import type { UpdatePatientInput } from "@src/packages/patients/update-patient-request";

type PregnancyOperations = NonNullable<
  NonNullable<UpdatePatientInput["obGynHistory"]>["pregnancies"]
>;

export function PregnancyScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("Pregnancy");
  const navigation = useNavigation();
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  const gender = state.sex;

  const selection = state.hasBeenPregnant ?? null;

  const isAMAB = gender === "AMAB";
  const title = isAMAB
    ? t("med_history.basic_info.pregnancy.title_amab")
    : t("med_history.basic_info.pregnancy.title_afab");
  const subtitle = isAMAB
    ? undefined
    : t("med_history.basic_info.pregnancy.subtitle");

  // Keep the count unanswered until the patient increments the control.
  const currentCount = state.pregnancyCount ?? null;
  const existingPregnancies = useMemo(
    () => patient?.obGynHistory?.pregnancies ?? [],
    [patient?.obGynHistory?.pregnancies],
  );

  const savePregnancyCount = useCallback(
    async (newCount: number) => {
      updateState({ pregnancyCount: newCount });

      let pregnancies: PregnancyOperations = existingPregnancies.map(
        (pregnancy) => ({ ...pregnancy }),
      );
      if (newCount > pregnancies.length) {
        for (let i = pregnancies.length; i < newCount; i++) {
          pregnancies.push({});
        }
      } else if (newCount < pregnancies.length) {
        const removed = pregnancies.slice(newCount);
        pregnancies = [
          ...pregnancies.slice(0, newCount),
          ...removed.map((pregnancy) => ({
            ...(typeof pregnancy.id === "string" ? { id: pregnancy.id } : {}),
            op: "delete" as const,
          })),
        ];
      }

      try {
        await updatePatient({
          obGynHistory: {
            id: patient?.obGynHistory?.id,
            pregnancies,
          },
        });
      } catch {
        return false;
      }
      return true;
    },
    [
      updateState,
      updatePatient,
      patient?.obGynHistory?.id,
      existingPregnancies,
    ],
  );

  // "No" is a complete answer, so move on immediately. "Yes" initializes
  // the conditional count to one and keeps the answer editable.
  const handleSelection = useCallback(
    async (value: boolean) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      const stateUpdates = {
        hasBeenPregnant: value,
        pregnancyCount: value ? (state.pregnancyCount ?? 1) : null,
      };
      updateState(stateUpdates);
      try {
        await updatePatient({
          obGynHistory: {
            id: patient?.obGynHistory?.id,
            hasBeenPregnant: value,
          },
        });
      } catch {
        return;
      }

      if (!value) {
        next(stateUpdates);
      }
    },
    [
      next,
      state.pregnancyCount,
      updateState,
      updatePatient,
      patient?.obGynHistory?.id,
    ],
  );

  const handleSave = useCallback(async () => {
    // Sync the answered count to the repeatable pregnancy records.
    if (
      selection === true &&
      state.pregnancyCount != null &&
      existingPregnancies.length === 0
    ) {
      const accepted = await savePregnancyCount(state.pregnancyCount);
      if (!accepted) return;
    }
    next();
  }, [
    selection,
    existingPregnancies.length,
    savePregnancyCount,
    next,
    state.pregnancyCount,
  ]);

  const hasCount = state.pregnancyCount != null;

  return (
    <BasicInfoQuestionLayout
      route="Pregnancy"
      title={title}
      subtitle={subtitle}
      helpText={t("med_history.basic_info.help.pregnancy")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection !== true || !hasCount || isPending}
      hideSaveButton={selection !== true}
      saveLabel={t("med_history.shared.next")}
    >
      <YesNoOptions
        bottomSpacing={16}
        noLabel={t("med_history.shared.no")}
        onChange={handleSelection}
        value={selection}
        yesLabel={t("med_history.shared.yes")}
      />

      {selection === true && (
        <View style={styles.counterContainer}>
          <CounterInput
            label={t("med_history.basic_info.pregnancy.counter_label")}
            description={t(
              "med_history.basic_info.pregnancy.counter_description",
            )}
            icon={Baby}
            value={currentCount}
            onIncrement={() =>
              savePregnancyCount(Math.max(1, (currentCount ?? 0) + 1))
            }
            onDecrement={() =>
              savePregnancyCount(Math.max(1, (currentCount ?? 1) - 1))
            }
            min={1}
            max={99}
          />
        </View>
      )}
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  counterContainer: {
    width: "100%",
    marginBottom: 40,
  },
});

export default PregnancyScreen;
